import { useState, useMemo } from 'react'
import { format, subDays } from 'date-fns'
import { useParams, useNavigate } from 'react-router-dom'
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table'
import Layout from '../../components/Layout'
import DateRangePicker from '../../components/DateRangePicker'
import MetricCard from '../../components/MetricCard'
import RequestDetailModal from '../../components/RequestDetailModal'
import { useAdminUserAnalytics, RequestRecordAdmin } from '../../api/admin'
import type { RequestRecord, PlanTier } from '../../types/analytics'

const COLORS = {
  approved: '#3fb950',
  rejected: '#f85149',
  inconclusive: '#d29922',
  error: '#8b949e',
}

const verdictColors: Record<string, string> = {
  APPROVED: 'bg-verdict-approved/20 text-verdict-approved',
  REJECTED: 'bg-verdict-rejected/20 text-verdict-rejected',
  INCONCLUSIVE: 'bg-verdict-inconclusive/20 text-verdict-inconclusive',
  ERROR: 'bg-noir-tertiary text-text-secondary',
}

const columnHelper = createColumnHelper<RequestRecordAdmin>()

export default function UserAnalytics() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 6), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [selectedRequest, setSelectedRequest] = useState<RequestRecord | null>(null)

  // Use admin-specific endpoint for user analytics
  const { data, isLoading, error } = useAdminUserAnalytics(
    userId || '',
    dateRange.start,
    dateRange.end
  )

  // Define table columns for requests breakdown
  const columns = useMemo(
    () => [
      columnHelper.accessor('timestamp', {
        header: 'Timestamp',
        cell: (info) => format(new Date(info.getValue()), 'MMM d, HH:mm'),
      }),
      columnHelper.accessor('verdict', {
        header: 'Verdict',
        cell: (info) => (
          <span className={`px-2 py-1 rounded-md text-xs font-medium ${verdictColors[info.getValue()]}`}>
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('file_extension', {
        header: 'File Type',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('content_lines', {
        header: 'Lines',
        cell: (info) => info.getValue().toLocaleString(),
      }),
      columnHelper.accessor('total_voting_rounds', {
        header: 'Rounds',
      }),
      columnHelper.accessor('total_input_tokens', {
        header: 'Input Tokens',
        cell: (info) => info.getValue().toLocaleString(),
      }),
      columnHelper.accessor('total_output_tokens', {
        header: 'Output Tokens',
        cell: (info) => info.getValue().toLocaleString(),
      }),
      columnHelper.accessor('cost_usd', {
        header: 'Cost',
        cell: (info) => `$${info.getValue().toFixed(4)}`,
      }),
      columnHelper.accessor('total_duration_ms', {
        header: 'Latency',
        cell: (info) => `${(info.getValue() / 1000).toFixed(1)}s`,
      }),
    ],
    []
  )

  const table = useReactTable({
    data: data?.requests || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div
              className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-accent-cyan border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
              aria-label="Loading analytics"
            >
              <span className="sr-only">Loading analytics...</span>
            </div>
            <p className="mt-4 text-sm text-text-secondary">Loading user analytics...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="rounded-md bg-verdict-rejected/10 border border-verdict-rejected/30 p-6">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-verdict-rejected"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-verdict-rejected">Error loading analytics</h3>
              <p className="mt-2 text-sm text-verdict-rejected">
                {error instanceof Error ? error.message : 'Failed to load analytics'}
              </p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  // Check for empty state
  const hasData = data && data.total_requests > 0

  // Prepare pie chart data (excluding errors - visible in request list)
  const verdictData = [
    { name: 'Approved', value: data?.approved || 0, color: COLORS.approved },
    { name: 'Rejected', value: data?.rejected || 0, color: COLORS.rejected },
    { name: 'Inconclusive', value: data?.inconclusive || 0, color: COLORS.inconclusive },
  ].filter((d) => d.value > 0)

  // Prepare line chart data for cost trend
  const costTrendData = (data?.analytics || []).map((day) => ({
    date: format(new Date(day.date), 'MMM d'),
    cost: day.total_cost_usd,
    requests: day.total_requests,
  }))

  // Prepare token breakdown data
  const tokenData = (data?.analytics || []).map((day) => ({
    date: format(new Date(day.date), 'MMM d'),
    input: day.total_input_tokens,
    output: day.total_output_tokens,
  }))

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex flex-col gap-4">
          <button
            onClick={() => navigate('/admin/analytics')}
            className="inline-flex items-center text-sm text-text-secondary hover:text-text-primary w-fit"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Analytics
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-text-primary font-mono">
                {data?.username ? `${data.username}'s Analytics` : 'User Analytics'}
              </h1>
              {data?.username && (
                <p className="mt-1 text-sm text-text-secondary">
                  User ID: {data.user_id}
                </p>
              )}
            </div>
            <DateRangePicker
              startDate={dateRange.start}
              endDate={dateRange.end}
              onChange={(start, end) => setDateRange({ start, end })}
            />
          </div>
        </div>

        {!hasData ? (
          <div className="card py-16">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-text-primary">No data for this period</h3>
              <p className="mt-1 text-sm text-text-secondary">
                This user has no activity for the selected date range.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Metric Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                title="Total Requests"
                value={data?.total_requests?.toLocaleString() || '0'}
              />
              <MetricCard
                title="Approval Rate"
                value={`${((data?.approval_rate || 0) * 100).toFixed(1)}%`}
              />
              <MetricCard
                title="Total Cost"
                value={`$${(data?.total_cost_usd || 0).toFixed(2)}`}
              />
              <MetricCard
                title="Avg Cost/Request"
                value={`$${(data?.avg_cost_per_request || 0).toFixed(4)}`}
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Verdict Distribution */}
              <div className="card">
                <h2 className="text-lg font-medium text-text-primary font-mono mb-4">
                  Verdict Distribution
                </h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={verdictData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                        }
                      >
                        {verdictData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#161b22',
                          border: '1px solid #30363d',
                          borderRadius: '6px',
                          color: '#e6edf3'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Cost Trend */}
              <div className="card">
                <h2 className="text-lg font-medium text-text-primary font-mono mb-4">
                  Daily Cost Trend
                </h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={costTrendData}>
                      <CartesianGrid stroke="#30363d" strokeDasharray="3 3" />
                      <XAxis dataKey="date" fontSize={12} stroke="#8b949e" tick={{ fill: '#8b949e' }} />
                      <YAxis
                        fontSize={12}
                        stroke="#8b949e"
                        tick={{ fill: '#8b949e' }}
                        tickFormatter={(value) => `$${value.toFixed(2)}`}
                      />
                      <Tooltip
                        formatter={(value: number | undefined) => [`$${(value ?? 0).toFixed(4)}`, 'Cost']}
                        contentStyle={{
                          backgroundColor: '#161b22',
                          border: '1px solid #30363d',
                          borderRadius: '6px',
                          color: '#e6edf3'
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="cost"
                        stroke="#58a6ff"
                        strokeWidth={2}
                        dot={{ fill: '#58a6ff' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Token Usage */}
            <div className="card">
              <h2 className="text-lg font-medium text-text-primary font-mono mb-4">
                Token Usage Over Time
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tokenData}>
                    <CartesianGrid stroke="#30363d" strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={12} stroke="#8b949e" tick={{ fill: '#8b949e' }} />
                    <YAxis
                      fontSize={12}
                      stroke="#8b949e"
                      tick={{ fill: '#8b949e' }}
                      tickFormatter={(value) =>
                        value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value
                      }
                    />
                    <Tooltip
                      formatter={(value: number | undefined) => [
                        (value ?? 0).toLocaleString(),
                        'Tokens',
                      ]}
                      contentStyle={{
                        backgroundColor: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: '6px',
                        color: '#e6edf3'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="input" name="Input Tokens" fill="#58a6ff" />
                    <Bar dataKey="output" name="Output Tokens" fill="#3fb950" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Requests Breakdown */}
            <div className="card">
              <h2 className="text-lg font-medium text-text-primary font-mono mb-4">
                Request Breakdown
              </h2>
              {data?.requests && data.requests.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-noir-tertiary">
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                            >
                              <button
                                onClick={header.column.getToggleSortingHandler()}
                                className="flex items-center gap-1 w-full hover:text-text-primary focus:outline-hidden focus:text-accent-cyan transition-colors"
                                aria-label={`Sort by ${header.column.columnDef.header as string}`}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                <span aria-hidden="true">
                                  {{
                                    asc: ' ↑',
                                    desc: ' ↓',
                                  }[header.column.getIsSorted() as string] ?? null}
                                </span>
                              </button>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody className="divide-y divide-border">
                      {table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          onClick={() => {
                            // Convert RequestRecordAdmin to RequestRecord shape
                            const request: RequestRecord = {
                              ...row.original,
                              plan_tier: row.original.plan_tier as PlanTier,
                              conversation_context_chars: 0,
                              project_context_chars: 0,
                              file_context_chars: 0,
                              extracted_intent_chars: 0,
                              voting_tokens: 0,
                              intent_tokens: 0,
                              summary_tokens: 0,
                              intent_extraction_ms: 0,
                              intent_source: 'none',
                              client_version: row.original.client_version || '',
                              editor_type: row.original.editor_type || '',
                              error_code: '',
                            }
                            setSelectedRequest(request)
                          }}
                          className="hover:bg-noir-tertiary cursor-pointer transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-cyan focus-visible:ring-inset"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              const request: RequestRecord = {
                                ...row.original,
                                plan_tier: row.original.plan_tier as PlanTier,
                                conversation_context_chars: 0,
                                project_context_chars: 0,
                                file_context_chars: 0,
                                extracted_intent_chars: 0,
                                voting_tokens: 0,
                                intent_tokens: 0,
                                summary_tokens: 0,
                                intent_extraction_ms: 0,
                                intent_source: 'none',
                                client_version: row.original.client_version || '',
                                editor_type: row.original.editor_type || '',
                                error_code: '',
                              }
                              setSelectedRequest(request)
                            }
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="px-4 py-4 text-sm text-text-primary whitespace-nowrap"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-text-secondary">
                  No requests found for this period
                </div>
              )}
              {data?.requests && data.requests.length > 0 && (
                <div className="mt-4 text-sm text-text-secondary">
                  Showing {data.requests.length} request{data.requests.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </>
        )}

        {/* Request Detail Modal */}
        {selectedRequest && (
          <RequestDetailModal
            request={selectedRequest}
            onClose={() => setSelectedRequest(null)}
            isAdmin={true}
          />
        )}
      </div>
    </Layout>
  )
}
